import {
	IconArrowsMaximize,
	IconChevronLeft,
	IconChevronRight,
	IconChevronsLeft,
	IconChevronsRight,
	IconFileDownload,
	IconRotate,
	IconX,
} from "@tabler/icons-react";
import { useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { Button } from "@/components/core/buttons/button";
import { Input } from "@/components/core/forms/input";
import { ScrollArea } from "@/components/core/scroller";
import Tooltip from "@/components/core/tooltip";

const maxWidth = 800;
pdfjs.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.8.69/pdf.worker.min.mjs";

interface SimplePdfViewerProps {
	fileUrl: string;
	fileName: string;
	onDownload?: () => void;
}

export function SimplePdfViewer({ fileUrl, fileName, onDownload }: SimplePdfViewerProps) {
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);
	const [rotateValue, setRotateValue] = useState(0);
	const [fullscreen, setFullscreen] = useState(false);

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const inputPage = Number.parseInt(event.target.value, 10);
		if (!Number.isNaN(inputPage) && inputPage >= 1 && inputPage <= totalPages) {
			setCurrentPage(inputPage);
		}
	};

	const handleDownload = () => {
		if (onDownload) {
			onDownload();
		}
	};

	return (
		<div className="flex flex-col shrink grow w-full bg-background overflow-y-hidden">
			<div className="grid grid-cols-3 shrink-0 w-full h-[50px] px-4 border-b border-slate-200">
				<div className="col-span-1 flex flex-row w-full items-center">
					<span className="text-base font-bold text-foreground-dark">{fileName}</span>
				</div>
				<div className="col-span-1 flex flex-row items-center w-full">
					<div className="flex flex-row justify-center w-full gap-1">
						<Tooltip content="Go to first page" side="bottom">
							<Button onClick={() => setCurrentPage(1)} type="button" size="icon" variant="ghost" disabled={currentPage === 1}>
								<IconChevronsLeft size={18} stroke={1.5} />
							</Button>
						</Tooltip>
						<Tooltip content="Previous page" side="bottom">
							<Button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} type="button" size="icon" variant="ghost">
								<IconChevronLeft size={18} stroke={1.5} />
							</Button>
						</Tooltip>
						<div className="flex flex-row items-center w-[100px] h-[28px]">
							<Input
								className="text-center text-sm font-semibold h-full border border-border/50 outline-none text-foreground rounded-md"
								value={currentPage}
								onChange={handleInputChange}
								min={1}
								max={totalPages}
								type="number"
							/>
							<span className="text-sm font-semibold inline-flex px-1 text-gray-300">/</span>
							<Input
								className="text-center text-sm font-semibold h-full border border-border/50 outline-none text-foreground rounded-md"
								value={totalPages}
								disabled
								type="number"
							/>
						</div>
						<Tooltip content="Next page" side="bottom">
							<Button onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage >= totalPages} type="button" size="icon" variant="ghost">
								<IconChevronRight size={18} stroke={1.5} />
							</Button>
						</Tooltip>
						<Tooltip content="Go to last page" side="bottom">
							<Button onClick={() => setCurrentPage(totalPages)} disabled={currentPage >= totalPages} type="button" size="icon" variant="ghost">
								<IconChevronsRight size={18} stroke={1.5} />
							</Button>
						</Tooltip>
					</div>
				</div>
				<div className="col-span-1 flex flex-row items-center w-full">
					<div className="flex flex-row justify-end w-full gap-1">
						<Tooltip content="Full screen" side="bottom">
							<Button onClick={() => setFullscreen(true)} type="button" size="icon" variant="ghost">
								<IconArrowsMaximize size={18} stroke={1.5} />
							</Button>
						</Tooltip>
						<Tooltip content="Rotate" side="bottom">
							<Button onClick={() => setRotateValue(rotateValue + 90)} type="button" size="icon" variant="ghost">
								<IconRotate size={18} stroke={1.5} />
							</Button>
						</Tooltip>
						{onDownload && (
							<Tooltip content="Download" side="bottom">
								<Button onClick={handleDownload} type="button" size="icon" variant="ghost">
									<IconFileDownload size={18} stroke={1.5} />
								</Button>
							</Tooltip>
						)}
					</div>
				</div>
			</div>
			<div className="flex flex-col items-center shrink grow w-full p-6 bg-slate-100 overflow-y-auto">
				<ScrollArea className="h-[calc(100svh-50px)]">
					<Document
						className="max-w-[800px]"
						rotate={rotateValue}
						file={fileUrl}
						onLoadSuccess={({ numPages }) => setTotalPages(numPages)}
						loading={<p>Loading PDF...</p>}
						error={<p>Failed to load PDF.</p>}
					>
						<Page pageNumber={currentPage} width={maxWidth} renderTextLayer={false} />
					</Document>
				</ScrollArea>
			</div>
			{fullscreen && (
				<div className="fixed inset-0 z-[999] bg-black overflow-auto">
					<div className="flex flex-row justify-end">
						<button className="p-4" type="button" onClick={() => setFullscreen(false)}>
							<IconX className="text-white" size={26} stroke={1.5} />
						</button>
					</div>
					<div className="flex flex-col items-center gap-4 py-4">
						<Document
							file={fileUrl}
							onLoadSuccess={({ numPages }) => setTotalPages(numPages)}
							loading={<p>Loading PDF...</p>}
							error={<p>Failed to load PDF.</p>}
						>
							{Array.from({ length: totalPages }, (_, i) => (
								<Page key={i} pageNumber={i + 1} scale={1.2} />
							))}
						</Document>
					</div>
				</div>
			)}
		</div>
	);
}
